<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';
import type { DateTimePickerProps } from '../types';
import DatePicker from '@/components/forms/DatePicker/DatePicker.vue';
import TimePicker from '@/components/forms/TimePicker/TimePicker.vue';
import { DateTime } from 'luxon';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import { watchEffect } from 'vue';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<DateTimePickerProps>(), {
  mode: 'edit',
});

const emits = defineEmits<{
  'update:modelValue': [dateTime: DateTime | undefined];
}>();

const passdownProps = computed(() => ({
  ...props,
  label: undefined,
  modelValue: undefined,
}));

const attrs = useAttrs();
const passdownAttrs = computed(() => ({
  ...attrs,
  class: undefined,
}));

const timeModel = ref<DateTime>(props.modelValue || DateTime.now());
const dateModel = ref<DateTime>(props.modelValue || DateTime.now());

watchEffect(() => {
  if (props.modelValue) {
    timeModel.value = props.modelValue;
    dateModel.value = props.modelValue;
  }
});

function update(dateTime: DateTime | undefined) {
  emits('update:modelValue', dateTime);
}
</script>

<template>
  <div :class="$attrs.class">
    <v-label v-if="label">{{ label }}</v-label>
    <p v-if="mode === 'details'">
      <DateTimeFormatter :value="modelValue" :format="formatForDetails" />
    </p>

    <v-row v-else>
      <v-col>
        <DatePicker
          v-bind="{ ...passdownProps, ...passdownAttrs }"
          :modelValue="dateModel"
          :data-testid="`${label}_Date`"
          @update:modelValue="update"
        />
      </v-col>
      <v-col>
        <TimePicker
          v-bind="{ ...passdownProps, ...passdownAttrs }"
          :modelValue="timeModel"
          :disabled="props.timeDisabled"
          :data-testid="`${label}_Time`"
          @update:modelValue="update"
        />
      </v-col>
    </v-row>
  </div>
</template>
